import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStudentDashboard } from '../../services/analytics';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, CheckCircle, FileText, BarChart3, Zap, Target, Flame, ArrowRight } from 'lucide-react';

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    pink: 'from-pink-500 to-pink-600',
    indigo: 'from-indigo-500 to-indigo-600',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${colors[color]} flex items-center justify-center text-white text-lg mb-3`}>{icon}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStudentDashboard().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div></div>;

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
        <h1 className="text-2xl font-bold mb-2">Xush kelibsiz, {user?.name}!</h1>
        <p className="text-blue-100">Bugungi o'quv rejangizni davom ettiring</p>
        <div className="flex items-center gap-6 mt-4">
          <div className="flex items-center gap-2 bg-white/20 rounded-lg px-4 py-2">
            <Zap className="w-4 h-4" /><span className="font-bold">{data?.xp_points || 0} XP</span>
          </div>
          <div className="flex items-center gap-2 bg-white/20 rounded-lg px-4 py-2">
            <Target className="w-4 h-4" /><span className="font-bold">Daraja {data?.level || 1}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/20 rounded-lg px-4 py-2">
            <Flame className="w-4 h-4" /><span className="font-bold">{data?.streak_days || 0} kun streak</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<BookOpen className="w-5 h-5" />} label="Yozilgan kurslar" value={data?.courses_enrolled || 0} color="blue" />
        <StatCard icon={<CheckCircle className="w-5 h-5" />} label="Tugatilgan" value={data?.courses_completed || 0} color="green" />
        <StatCard icon={<FileText className="w-5 h-5" />} label="Tugatilgan darslar" value={data?.total_lessons_completed || 0} color="purple" />
        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="O'rtacha ball" value={data?.avg_test_score ? Math.round(data.avg_test_score) + '%' : '-'} color="orange" />
      </div>

      {/* Courses */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Mening kurslarim</h2>
          <Link to="/courses" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 text-sm font-medium">Barcha kurslar →</Link>
        </div>
        {data?.enrollments?.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.enrollments.map(e => (
              <Link key={e.id} to={`/learn/${e.course?.id}`} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition group">
                <div className="h-36 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 flex items-center justify-center"><BookOpen className="w-12 h-12 text-blue-400" /></div>
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-blue-600 transition">{e.course?.title}</h3>
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${e.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {e.status === 'completed' ? 'Tugatilgan' : 'Davom etmoqda'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700">
            <BookOpen className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Hali kursga yozilmagansiz</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Kurslar katalogidan o'zingizga mos kursni tanlang</p>
            <Link to="/courses" className="inline-flex px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition">Kurslar katalogi →</Link>
          </div>
        )}
      </div>
    </div>
  );
}